import { useCallback, useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import domToImage from 'dom-to-image-more'
import '@itsce-geospatial/d3-equal-earth-map-component'
import './worldmapTooltip.css'
import { isValidDataForColorScale, parseColorToRGB } from '../../utils/helper'
import ChartCardContentLoader from '../CardContentLoader/ChartCardContentLoader'
import { BinTypes, Breakpoint, ScaleTypes } from '../../utils/enum'
import { COLOR_MAP } from '../../utils/theme'
import useDeviceIdentification from '../../hooks/useDeviceIdentification'
import { WORLD_DEFAULT } from '../../utils/constants'

const WorldMap = ({
  getTooltipContent,
  features,
  countryCentroids,
  getColorScale,
  selectedCountries,
  indicatorCode,
  isTheme,
  isTopic,
  scaleType,
  countryFocus = '',
  missingDataColor,
  binType,
  legendRange,
}) => {
  let width = 600
  let height = isTheme ? 450 : 250
  const isMobile = useDeviceIdentification(Breakpoint.Mobile)

  const worldMapContainerRef = useRef(null)
  const worldmapSvgRef = useRef(null)
  const [mapWidth, setMapWidth] = useState(width)
  const [mapHeight, setMapHeight] = useState(height)
  const [geojson, setGeojson] = useState(null)
  const [mapCenter, setMapCenter] = useState([15, 11])
  const [mapZoom, setMapZoom] = useState({
    initial: isMobile ? 0.39 : isTopic ? 1 : 0.7,
    min: isMobile ? 0.39 : isTopic ? 1 : 0.7,
    max: 15,
  })
  const [geoJsonKey, setGeoJsonKey] = useState(0)

  useEffect(() => {
    const containerWidth = worldMapContainerRef?.current?.offsetWidth
    if (containerWidth) {
      setMapWidth(containerWidth)
      const height = containerWidth / 2
      if (isTopic && height > 420) {
        setMapHeight(420)
      } else {
        setMapHeight(height)
      }
    }
  }, [])

  useEffect(() => {
    if (features?.length > 0) {
      const geojsonData = prepareGeojson(
        features,
        getColorScale,
        selectedCountries,
        missingDataColor,
        binType,
        legendRange,
        scaleType
      )
      setGeojson(geojsonData)
      const currentGeojsonString = JSON.stringify(geojsonData)
      if (geojson !== currentGeojsonString) {
        setGeoJsonKey((prev) => prev + 1)
      }
    }
  }, [
    features,
    getColorScale,
    selectedCountries,
    missingDataColor,
    binType,
    legendRange,
    scaleType,
    mapHeight,
  ])

  const prepareGeojson = (
    features,
    getColorScale,
    selectedCountries,
    missingDataColor,
    binType,
    legendRange,
    scaleType
  ) => {
    const noDataColor = missingDataColor
      ? missingDataColor
      : COLOR_MAP.not_available

    const styledFeatures = features.map((feature) => {
      let color

      if (feature.properties.disputed_iso3) {
        const [code1, code2] = feature.properties.disputed_iso3.split('-')

        const country1 = features.find((f) => f.properties.ISO_A3 === code1)
        const country2 = features.find((f) => f.properties.ISO_A3 === code2)

        const value1 = country1 ? country1.properties.value : null
        const value2 = country2 ? country2.properties.value : null

        const color1 =
          value1 !== undefined && value1 !== null
            ? getColorScale(value1)
            : noDataColor
        const color2 =
          value2 !== undefined && value2 !== null
            ? getColorScale(value2)
            : noDataColor

        const rgb1 = parseColorToRGB(color1, noDataColor)
        const rgb2 = parseColorToRGB(color2, noDataColor)

        const averageRGB = {
          r: Math.round((rgb1.r + rgb2.r) / 2),
          g: Math.round((rgb1.g + rgb2.g) / 2),
          b: Math.round((rgb1.b + rgb2.b) / 2),
        }

        color = `rgb(${averageRGB.r}, ${averageRGB.g}, ${averageRGB.b})`
      } else {
        const value = feature.properties.value
        color = getColorScale
          ? scaleType === ScaleTypes.Categorical
            ? value !== '' &&
              value !== undefined &&
              value !== 'null' &&
              value !== 'NaN' &&
              value !== null
              ? getColorScale(value)
              : noDataColor
            : isValidDataForColorScale(value)
            ? binType === BinTypes.StandardDeviation
              ? getColorScale(value, legendRange)
              : getColorScale(value)
            : noDataColor
          : noDataColor
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          color,
          fillOpacity: 0.8,
        },
      }
    })

    return JSON.stringify({
      type: 'FeatureCollection',
      features: styledFeatures,
    })
  }

  const exportAsImage = (filename, node) => {
    domToImage
      .toPng(node, {
        filter: (n) =>
          !n.classList?.contains('download-exclude') &&
          !n.classList?.contains('worldmap-tooltip'),
      })
      .then((dataUrl) => {
        const link = document.createElement('a')
        link.download = filename
        link.href = dataUrl
        link.click()
      })
  }

  const createTooltip = (feature, position) => {
    if (feature.properties.disputed_iso3) {
      return
    }
    d3.select(`#worldmap-${indicatorCode} .worldmap-tooltip`).remove()

    const tooltipContent = getTooltipContent(feature)
    const tooltip = d3
      .select(`#worldmap-${indicatorCode}`)
      .append('div')
      .attr('class', `worldmap-tooltip worldmap-tooltip-${indicatorCode}`)
      .html(tooltipContent)
      .style('position', 'absolute')
      .style('z-index', 10700)
      .style('display', 'block')
      .style('margin', '0')
      .style('font-size', '12px')
      .style('word-wrap', 'break-word')
      .style('background-color', COLOR_MAP.map.tooltip.background)
      .style('border', `solid ${COLOR_MAP.map.tooltip.border} 1px`)
      .style('border-radius', '2px')
      .style('padding', '5px')
      .style('width', 'auto')
      .style('visibility', 'visible')
      .style('pointer-events', 'none')

    const { x, y } = position
    tooltip.style('left', `${x}px`).style('top', `${y}px`)

    const containerRect = worldMapContainerRef?.current?.getBoundingClientRect()
    const tooltipRect = tooltip?.node()?.getBoundingClientRect()
    if (x + tooltipRect?.width > containerRect?.width) {
      tooltip?.style(
        'left',
        `${containerRect?.width - tooltipRect?.width - 10}px`
      )
    }
    if (y + tooltipRect.height > containerRect.height) {
      tooltip.style(
        'top',
        `${containerRect.height - tooltipRect.height - 10}px`
      )
    }
  }

  useEffect(() => {
    const mapElement = worldmapSvgRef.current
    if (!mapElement || !worldMapContainerRef.current) return

    const handleMapClick = (event) => {
      d3.select(`#worldmap-${indicatorCode} .worldmap-tooltip`).remove()

      const { detail } = event
      const feature = detail?.feature
      if (feature && !feature.properties.disputed_iso3) {
        const containerRect =
          worldMapContainerRef.current.getBoundingClientRect()
        const x = detail.mouseEvent.clientX - containerRect.left + 10
        const y = detail.mouseEvent.clientY - containerRect.top + 10
        createTooltip(feature, { x, y })
      }
    }

    mapElement.addEventListener('mapClick', handleMapClick)
    return () => mapElement.removeEventListener('mapClick', handleMapClick)
  }, [getTooltipContent, indicatorCode, isTheme, isTopic, geojson])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(`.worldmap-tooltip-${indicatorCode}`) &&
        !event.target.closest(`#worldmapSvg-${indicatorCode}`)
      ) {
        d3.select(`#worldmap-${indicatorCode} .worldmap-tooltip`).remove()
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [indicatorCode])

  useEffect(() => {
    const countriesArray = Array.isArray(selectedCountries)
      ? selectedCountries
      : []

    const filteredCountries = countriesArray.filter(
      (country) => country !== WORLD_DEFAULT.id
    )

    if (filteredCountries.length === 1) {
      const selectedCountryIso3 = filteredCountries[0]
      const countryData = countryCentroids?.country_centroids?.find(
        (country) => country.iso3 === selectedCountryIso3
      )
      if (countryData) {
        const [longitude, latitude] = countryData.centroids
          .replace('POINT (', '')
          .replace(')', '')
          .split(' ')
          .map(parseFloat)
        setMapCenter([longitude, latitude])
        setMapZoom({
          initial: 3,
          min: isMobile ? 0.39 : isTopic ? 1 : 0.7,
          max: 15,
        })

        const selectedFeature = features.find(
          (feature) => feature.properties.ISO_A3 === selectedCountryIso3
        )
        if (selectedFeature) {
          const containerRect =
            worldMapContainerRef.current.getBoundingClientRect()
          const centerX = containerRect.width / 2
          const centerY = containerRect.height / 2
          createTooltip(selectedFeature, { x: centerX, y: centerY })
        }
      }
    } else {
      setMapCenter([15, 11])
      setMapZoom({
        initial: isMobile ? 0.39 : isTopic ? 1 : 0.7,
        min: isMobile ? 0.39 : isTopic ? 1 : 0.7,
        max: 15,
      })
      d3.select(`#worldmap-${indicatorCode} .worldmap-tooltip`).remove()
    }
  }, [selectedCountries, features, getTooltipContent, indicatorCode])

  return (
    <div className="tui__chart_content">
      <div className="button-container download-exclude">
        {isTheme ? (
          ''
        ) : (
          <div
            className="download-icon"
            onClick={() =>
              exportAsImage(
                `${indicatorCode}_worldmap.png`,
                worldMapContainerRef.current
              )
            }
          >
            <span className="lp lp-download"></span>
          </div>
        )}
      </div>
      <div className="chart-container">
        <div
          id={`worldmap-${indicatorCode}`}
          ref={worldMapContainerRef}
          style={{
            background: 'white',
            height: mapHeight,
            position: 'relative',
          }}
        >
          {features?.length > 0 && geojson ? (
            <d3-map
              id={`worldmapSvg-${indicatorCode}`}
              key={`worldmapsvg-key-${indicatorCode}`}
              ref={worldmapSvgRef}
              className="map"
              opacity="7"
              selectedprojection="natural_earth"
              geojson={geojson}
              countryNamesEnabled="true"
              foreground={COLOR_MAP.map.foreground}
              borderColor={COLOR_MAP.map.border}
              labelColor={COLOR_MAP.map.label}
              pointVisibilityThreshold="2"
              center={JSON.stringify(mapCenter)}
              zoom={JSON.stringify(mapZoom)}
            ></d3-map>
          ) : (
            <ChartCardContentLoader />
          )}
        </div>
      </div>
    </div>
  )
}
export default WorldMap
